import { useCallback, useEffect, useRef, useState } from "react"
import { GridRowId } from "@mui/x-data-grid-premium"
import { GO_TO_ROW_FLASH_DURATION_MS as FLASH_DURATION_MS } from "../constants"

export interface UseGoToRowFlashResult {
  flashingRowId: GridRowId | null
  triggerFlash: (rowId: GridRowId) => void
}

/**
 * Briefly flags a single row id as "flashing" so the grid can apply a transient highlight class
 * to it, then clears itself after FLASH_DURATION_MS.
 *
 * The clear timer is stored in a ref, mirroring useCellValueFlash, so an unrelated re-render
 * cannot cancel an in-flight flash — only a fresh triggerFlash call or unmount does.
 */
export function useGoToRowFlash(): UseGoToRowFlashResult {
  const [flashingRowId, setFlashingRowId] = useState<GridRowId | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const triggerFlash = useCallback((rowId: GridRowId) => {
    clearTimeout(timerRef.current ?? undefined)
    setFlashingRowId(rowId)
    timerRef.current = setTimeout(() => setFlashingRowId(null), FLASH_DURATION_MS)
  }, [])

  useEffect(() => () => clearTimeout(timerRef.current ?? undefined), [])

  return { flashingRowId, triggerFlash }
}
